import { useStyletron } from 'parkdepot-shared/theme'
import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// components
import Input from '../../../../components/Input/Input.component'

// style components
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import useEmailValidation from 'parkdepot-shared/utils/hooks/useEmailValidation'
import { useHistory } from 'react-router-dom'
import Text from '../../../../components/style/Text.style'
import { CheckoutContext } from '../../../../context/CheckoutContext/checkout.context'
import { useGetUser } from '../../../../hooks/useAuth'
import { Checkout } from '../../../../types/checkout.interface'
import { checkoutRouteConfirmationStep } from '../../route'
import NextButtonContainer from '../../style/NextButtonContainer.style'
import { Container, EmailInfo, EmailInputContainer } from './CustomerStep.style'
import ReactDOM from 'react-dom'

interface Props {
    bottomRef: React.RefObject<HTMLDivElement>
}

const CustomerStep: React.FC<Props> = ({ bottomRef }) => {
    const { t } = useTranslation()
    const [css, { typography }] = useStyletron()
    const validateEmail = useEmailValidation()
    const history = useHistory()

    const [inputEmail, setInputEmail] = useState('')
    const [nextButtonEnabled, setNextButtonEnabled] = useState(false)

    const { checkout, setEmail, modifyAndSetCheckout } = useContext(CheckoutContext)
    const { user } = useGetUser()

    useEffect(() => {
        // if email already filled in during checkout process, use that email
        if (checkout && checkout.email) {
            setInputEmail(checkout.email)
            setNextButtonEnabled(true)
            return
        }

        // if user is logged in, use user's email
        if (user) {
            setInputEmail(user.email)
            setEmail(user.email)
            setNextButtonEnabled(true)
        }
    }, [user, checkout, setEmail])

    const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputEmail(e.target.value)
        setEmail(e.target.value)

        if (validateEmail(e.target.value)) {
            setNextButtonEnabled(true)
        } else {
            setNextButtonEnabled(false)
        }
    }

    const onNextClick = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (validateEmail(inputEmail)) {
            modifyAndSetCheckout({ ...checkout, email: inputEmail } as Checkout)
            history.push(checkoutRouteConfirmationStep())
        }
    }

    return (
        <Container>
            <Text fontWeight={300} typography={typography.ParagraphXSmall} color="#667085">
                {t('common.emailPlaceholder')}
            </Text>
            <form id="customer-step-form" onSubmit={onNextClick}>
                <EmailInputContainer>
                    <div
                        className={css({
                            height: '5rem'
                        })}
                    >
                        <Input
                            data-testid="email-input"
                            value={inputEmail}
                            type="email"
                            onChange={onEmailChange}
                            placeholder={t('common.emailPlaceholder')}
                        />
                    </div>
                </EmailInputContainer>
                <EmailInfo>
                    <span>{t('checkout.payment.emailInfo')}</span>
                </EmailInfo>
                {bottomRef.current &&
                    ReactDOM.createPortal(
                        <NextButtonContainer>
                            <Button
                                id="customer-step-next-button"
                                form="customer-step-form"
                                data-testid="continue-to-confirmation-step-button"
                                type="submit"
                                disabled={!nextButtonEnabled}
                                $size={BUTTON_SIZE.medium}
                                $kind={BUTTON_KIND.primary}
                                overrides={{
                                    BaseButton: {
                                        style: {
                                            width: '100%'
                                        }
                                    }
                                }}
                            >
                                <div>{t('checkout.payment.nextStep')}</div>
                            </Button>
                        </NextButtonContainer>,
                        bottomRef.current
                    )}
            </form>
        </Container>
    )
}

export default CustomerStep
